// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  TagObjectType,
  type TagEntity,
  type TagsByObjectKey,
  type TagsByObjectEntry,
} from '@shared/data-access/interfaces/redux/tags-management-state-types';
import { generateAssignmentId } from '@shared/data-access/slices/tags-management';
import { groupBy } from '@shared/ui-kit';
import { maskIban } from '@shared/advisory/aggregation/utils/bank-utils';
import { type MouseEvent, useCallback, useMemo, useState } from 'react';
import { enableMapSet, produce } from 'immer';
import { useTranslation } from 'react-i18next';

enableMapSet();

export interface UseAccountsSelectorArgs {
  data?: Array<{
    iban: string;
    name: string;
    institution_id: string;
    institution_name: string;
    cash: number;
  }>;
  onChange?: (accounts: string[]) => void;
  selectedAccounts?: string[];
  currency?: string;
  tags?: readonly TagEntity[];
  selectedTag?: string;
  onTagSelect?: (tagId: string | undefined) => void;
  tagsByObject?: Map<TagsByObjectKey, TagsByObjectEntry>;
}

interface AccountsSelectorDraft {
  readonly tagId: string;
  readonly accounts: Set<string>;
}

type InstitutionOptionGroup = Array<{
  value: string;
  label: string;
  options: Array<{
    value: string;
    label: string;
    balance: number;
    iban: string;
  }>;
}>;

export const useAccountsSelector = ({
  data = [],
  onChange,
  selectedAccounts = [],
  tags,
  selectedTag,
  onTagSelect,
  tagsByObject,
}: UseAccountsSelectorArgs) => {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const [draft, setDraft] = useState<AccountsSelectorDraft | null>(null);

  const open = useMemo(() => Boolean(anchorEl), [anchorEl]);

  const normalizedSelectedTag = useMemo(() => selectedTag ?? '', [selectedTag]);

  const getFilteredDataByTag = useCallback(
    (tagId: string | undefined) => {
      if (!tagId || !tagsByObject) return data;

      return data.filter(
        account =>
          tagsByObject
            .get(
              generateAssignmentId(
                TagObjectType.Account,
                account.iban,
              ) as TagsByObjectKey,
            )
            ?.tags.some(tag => tag.tag_id.toString() === tagId) ?? false,
      );
    },
    [data, tagsByObject],
  );

  const buildOptionsFromData = useCallback(
    (filtered: typeof data) =>
      Array.from(
        groupBy(filtered, 'institution_id').entries(),
        ([id, accounts]) => ({
          value: id,
          label: accounts[0].institution_name,
          options: accounts.map(account => ({
            value: account.iban,
            label: account.name,
            balance: account.cash,
            iban: maskIban(account.iban, 'last-only'),
          })),
        }),
      ),
    [],
  );

  const optionsByTag = useMemo(
    () =>
      new Map<string, InstitutionOptionGroup>([
        ['', buildOptionsFromData(data)],
        ...(tags ?? []).map(
          tag =>
            [
              tag.tag_id.toString(),
              buildOptionsFromData(getFilteredDataByTag(tag.tag_id.toString())),
            ] as const,
        ),
      ]),
    [data, tags, getFilteredDataByTag, buildOptionsFromData],
  );

  const id = useMemo(() => (open ? 'accounts-popover' : undefined), [open]);

  const effectiveTag = useMemo(
    () => draft?.tagId ?? normalizedSelectedTag,
    [draft?.tagId, normalizedSelectedTag],
  );

  const accountSelected = useMemo(
    () => draft?.accounts ?? new Set(selectedAccounts),
    [draft, selectedAccounts],
  );

  const options = useMemo(
    () => optionsByTag.get(effectiveTag) ?? [],
    [optionsByTag, effectiveTag],
  );

  const selectedInstitutions = useMemo(
    () =>
      new Set(
        options
          .filter(option =>
            option.options.every(account => accountSelected.has(account.value)),
          )
          .map(option => option.value),
      ),
    [options, accountSelected],
  );

  const hasAllAccountsSelected = useMemo(
    () =>
      options.every(option =>
        option.options.every(account => accountSelected.has(account.value)),
      ),
    [options, accountSelected],
  );

  const hasAllOptionsSelected = useMemo(() => {
    const appliedSet = new Set(selectedAccounts);
    const appliedIbans = (optionsByTag.get(normalizedSelectedTag) ?? []).flatMap(
      group => group.options.map(account => account.value),
    );

    return (
      appliedIbans.length > 0 && appliedIbans.every(iban => appliedSet.has(iban))
    );
  }, [selectedAccounts, normalizedSelectedTag, optionsByTag]);

  const selectedTagLabel = useMemo(() => {
    if (normalizedSelectedTag === '') return undefined;
    return (
      tags?.find(tag => tag.tag_id.toString() === normalizedSelectedTag)?.name ??
      t('aggregator.all-accounts', { ns: 'aggregator' })
    );
  }, [normalizedSelectedTag, tags, t]);

  const tagOptions = useMemo(() => {
    const tagsWithAccounts = (tags ?? []).filter(tag =>
      (optionsByTag.get(tag.tag_id.toString()) ?? []).some(
        group => group.options.length > 0,
      ),
    );

    return [
      { id: '', name: t('common.all_female', { ns: 'common' }) },
      ...tagsWithAccounts.map(tag => ({
        id: tag.tag_id.toString(),
        name: tag.name,
      })),
    ].map(tag => ({
      ...tag,
      isActive: tag.id === '' ? effectiveTag === '' : effectiveTag === tag.id,
    }));
  }, [tags, t, effectiveTag, optionsByTag]);

  const selectAllLabel = useMemo(
    () =>
      hasAllAccountsSelected
        ? t('aggregator.deselect-all', { ns: 'aggregator' })
        : t('aggregator.select-all', { ns: 'aggregator' }),
    [hasAllAccountsSelected, t],
  );

  const onClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      setDraft({
        tagId: normalizedSelectedTag,
        accounts: new Set(selectedAccounts),
      });
      setAnchorEl(event.currentTarget);
    },
    [normalizedSelectedTag, selectedAccounts],
  );

  const onClose = useCallback(() => {
    setAnchorEl(null);
    setDraft(null);
  }, []);

  const onApplyChanges = useCallback(() => {
    onChange?.(Array.from(accountSelected));
    onTagSelect?.(draft?.tagId ?? normalizedSelectedTag);
    setAnchorEl(null);
    setDraft(null);
  }, [accountSelected, draft, onChange, onTagSelect, normalizedSelectedTag]);

  const onSelectAccount = useCallback((account: string) => {
    setDraft(prev =>
      prev === null
        ? prev
        : produce(prev, draft => {
            draft.accounts.has(account)
              ? draft.accounts.delete(account)
              : draft.accounts.add(account);
          }),
    );
  }, []);

  const onSelectInstitution = useCallback(
    (institution: string) => {
      const accounts =
        options.find(option => option.value === institution)?.options ?? [];

      if (accounts.length === 0) return;

      setDraft(prev =>
        prev === null
          ? prev
          : produce(prev, draft => {
              if (selectedInstitutions.has(institution)) {
                accounts.forEach(account => {
                  draft.accounts.delete(account.value);
                });
              } else {
                accounts.forEach(account => {
                  draft.accounts.add(account.value);
                });
              }
            }),
      );
    },
    [options, selectedInstitutions],
  );

  const onSelectAllAccounts = useCallback(() => {
    setDraft(prev =>
      prev === null
        ? prev
        : produce(prev, draft => {
            draft.accounts = new Set(
              options.flatMap(group => group.options.map(account => account.value)),
            );
          }),
    );
  }, [options]);

  const onClearAllAccounts = useCallback(() => {
    setDraft(prev =>
      prev === null
        ? prev
        : produce(prev, draft => {
            options
              .flatMap(group => group.options.map(account => account.value))
              .forEach(iban => draft.accounts.delete(iban));
          }),
    );
  }, [options]);

  const onPendingTagSelect = useCallback(
    (tagId: string | undefined) => {
      const nextTagId = tagId ?? '';

      setDraft(prev =>
        prev === null
          ? prev
          : produce(prev, draft => {
              draft.tagId = nextTagId;
              draft.accounts = new Set(
                (optionsByTag.get(nextTagId) ?? []).flatMap(group =>
                  group.options.map(account => account.value),
                ),
              );
            }),
      );
    },
    [optionsByTag],
  );

  const selectedAccountsLabel = useMemo(() => {
    if (selectedTagLabel) {
      return selectedTagLabel;
    }

    if (options.length === 0) {
      return t('aggregator.no-accounts', { ns: 'aggregator' });
    }

    switch (selectedAccounts.length) {
      case 0:
        return t('aggregator.select-accounts', { ns: 'aggregator' });

      case 1:
        return t('aggregator.account_one', { ns: 'aggregator' });

      default:
        if (hasAllOptionsSelected) {
          return t('aggregator.all-accounts', { ns: 'aggregator' });
        }

        return t('aggregator.account_other', {
          count: selectedAccounts.length,
          ns: 'aggregator',
        }) as string;
    }
  }, [
    selectedTagLabel,
    options.length,
    selectedAccounts.length,
    hasAllOptionsSelected,
    t,
  ]);

  const hasAccounts = useMemo(() => data.length > 0, [data.length]);

  const hasPendingChanges = useMemo(() => {
    if (!draft) return false;

    const appliedSet = new Set(selectedAccounts);

    if (appliedSet.size !== accountSelected.size) return true;

    if (Array.from(appliedSet).some(account => !accountSelected.has(account)))
      return true;

    return normalizedSelectedTag !== draft.tagId;
  }, [draft, selectedAccounts, accountSelected, normalizedSelectedTag]);

  const canApply = useMemo(
    () => hasPendingChanges && options.length > 0 && accountSelected.size > 0,
    [hasPendingChanges, options.length, accountSelected.size],
  );

  return {
    anchorEl,
    open,
    id,
    onClick,
    onClose,
    options,
    selectedAccountsLabel,
    hasAllAccountsSelected,
    onSelectAccount,
    onSelectInstitution,
    onSelectAllAccounts,
    onClearAllAccounts,
    selectedInstitutions,
    accountSelected,
    onApplyChanges,
    hasAllOptionsSelected,
    hasAccounts,
    hasPendingChanges,
    canApply,
    selectAllLabel,
    tagOptions,
    onTagSelect: onPendingTagSelect,
  } as const;
};
